// Navigation helpers: bearing, distance, turn detection, nearest-turn-ahead.
// All lat/lng in degrees, distances in km.

const R_KM = 6371;
const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

export function haversineKm(a, b) {
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.sqrt(x));
}

export function bearingDeg(a, b) {
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const dLng = toRad(b[1] - a[1]);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Normalize a signed bearing delta to [-180, 180]
export function bearingDelta(fromDeg, toDeg2) {
  let d = ((toDeg2 - fromDeg + 540) % 360) - 180;
  return d;
}

/**
 * Extract turn points from a polyline.
 * A "turn" is any polyline vertex where the outgoing bearing differs from
 * the incoming bearing by more than `minAngle` degrees. Points too close
 * together are merged so we don't flag micro-zig-zags on switchbacks.
 *
 * Returns [{index, lat, lng, delta, direction, cumKm}] where direction is
 * "left" | "right" | "u-turn" and cumKm is the distance along the polyline
 * from the start.
 */
export function extractTurns(polyline, { minAngle = 55, minSegmentM = 60, smoothSpanM = 150, smoothMaxAngle = 35 } = {}) {
  if (!polyline || polyline.length < 3) return [];
  const turns = [];
  let cum = 0;
  const cumArr = [0];
  for (let i = 1; i < polyline.length; i++) {
    cum += haversineKm(polyline[i - 1], polyline[i]);
    cumArr.push(cum);
  }
  for (let i = 1; i < polyline.length - 1; i++) {
    // Short look-back / forward — catches the local bearing change.
    let back = i - 1;
    while (back > 0 && haversineKm(polyline[back], polyline[i]) * 1000 < minSegmentM) {
      back -= 1;
    }
    let fwd = i + 1;
    while (
      fwd < polyline.length - 1 &&
      haversineKm(polyline[i], polyline[fwd]) * 1000 < minSegmentM
    ) {
      fwd += 1;
    }
    const b1 = bearingDeg(polyline[back], polyline[i]);
    const b2 = bearingDeg(polyline[i], polyline[fwd]);
    const delta = bearingDelta(b1, b2);
    if (Math.abs(delta) < minAngle) continue;

    // ---- SMOOTH-CURVE FILTER (Phase 2, User Directive #2) ----
    // If the same delta measured over a MUCH longer span (≈150m each side)
    // is very small (<smoothMaxAngle), we're looking at a smooth 90° bend of
    // a main road, NOT a real bifurcation/intersection. Skip it — otherwise
    // the app would say "svolta a destra" on every gentle main-road curve.
    let longBack = i - 1;
    while (longBack > 0 && haversineKm(polyline[longBack], polyline[i]) * 1000 < smoothSpanM) {
      longBack -= 1;
    }
    let longFwd = i + 1;
    while (
      longFwd < polyline.length - 1 &&
      haversineKm(polyline[i], polyline[longFwd]) * 1000 < smoothSpanM
    ) {
      longFwd += 1;
    }
    // Only apply the filter if we actually have enough polyline on both
    // sides — near the endpoints the long span isn't representative.
    const haveLongContext =
      haversineKm(polyline[longBack], polyline[i]) * 1000 >= smoothSpanM * 0.6 &&
      haversineKm(polyline[i], polyline[longFwd]) * 1000 >= smoothSpanM * 0.6;
    if (haveLongContext) {
      const lb = bearingDeg(polyline[longBack], polyline[i]);
      const lf = bearingDeg(polyline[i], polyline[longFwd]);
      const longDelta = Math.abs(bearingDelta(lb, lf));
      // Smooth curve: local delta big but long-span delta is small → skip.
      if (longDelta < smoothMaxAngle) continue;
    }

    const direction =
      Math.abs(delta) > 150 ? "u-turn" : delta > 0 ? "right" : "left";
    turns.push({
      index: i,
      lat: polyline[i][0],
      lng: polyline[i][1],
      delta, // signed, -180..180
      direction,
      cumKm: cumArr[i],
    });
  }
  // De-duplicate turns that are within 40m of each other, keep the sharpest.
  const dedup = [];
  for (const t of turns) {
    const last = dedup[dedup.length - 1];
    if (last && haversineKm([last.lat, last.lng], [t.lat, t.lng]) * 1000 < 40) {
      if (Math.abs(t.delta) > Math.abs(last.delta)) {
        dedup[dedup.length - 1] = t;
      }
    } else {
      dedup.push(t);
    }
  }
  return dedup;
}

/**
 * Find the polyline point closest to `pos`, and its progress along the line.
 * Returns {index, cumKm, distanceKm} where distanceKm is the perpendicular-
 * ish distance from pos to that segment endpoint.
 */
export function projectOnPolyline(pos, polyline) {
  let best = { index: 0, distanceKm: Infinity };
  for (let i = 0; i < polyline.length; i++) {
    const d = haversineKm(pos, polyline[i]);
    if (d < best.distanceKm) best = { index: i, distanceKm: d };
  }
  return best;
}

/**
 * Given a user position and known turns, find the NEXT upcoming turn along
 * the polyline (i.e. turn with polyline index >= user's projected index).
 * Returns the turn record enriched with `distanceKm` = straight-line distance
 * from `pos` to the turn point.
 */
export function nextTurnAhead(pos, polyline, turns) {
  if (!pos || !turns?.length) return null;
  const proj = projectOnPolyline(pos, polyline);
  for (const t of turns) {
    if (t.index >= proj.index) {
      return { ...t, distanceKm: haversineKm(pos, [t.lat, t.lng]) };
    }
  }
  // If past all turns (near end of loop), return the first turn (loop wraps)
  const t = turns[0];
  return { ...t, distanceKm: haversineKm(pos, [t.lat, t.lng]) };
}
